import { useState, useEffect, useCallback } from "react";
import axiosInstance from "@/hooks/axios";
import { isAxiosError } from "axios";
import { State } from "@/providers/UserProvider";
import { useRefreshToken } from "@/hooks";
import { User } from "@/types/user";
import * as Sentry from "@sentry/react";
import { parseUserDates } from "../handlers/parseUserDates";

export const useUser = (userId: User["id"], authToken: State["authToken"]) => {
  const [data, setData] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { refreshAccessToken } = useRefreshToken();

  const fetchUser = useCallback(async () => {
    if (!userId || userId <= 0 || !authToken) {
      return null;
    }

    setLoading(true);
    setError(null);

    const fetchWithToken = async (token: string) => {
      const response = await axiosInstance.get<User>(`users/users/${userId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const parsedUser = parseUserDates(response.data);

      setData(parsedUser);
      return parsedUser;
    };

    try {
      return await fetchWithToken(authToken);
    } catch (err: unknown) {
      if (
        isAxiosError(err) &&
        err.response?.status === 401 &&
        err.response.data?.code === "token_not_valid"
      ) {
        // Token is expired — attempt to refresh it
        const newAccessToken = await refreshAccessToken();

        if (newAccessToken) {
          try {
            return await fetchWithToken(newAccessToken);
          } catch (retryErr) {
            setError("Failed to fetch user after refreshing token.");
          }
        } else {
          setError("Session expired. Please log in again.");
        }
      } else if (isAxiosError(err)) {
        Sentry.captureException(err, {
          contexts: {
            axiosResponse: {
              url: err.config?.url,
              method: err.config?.method,
              status: err.response?.status,
              data: err.response?.data,
            },
          },
        });
        setError(err.message);
      } else if (err instanceof Error) {
        Sentry.captureException(err);
        setError(err.message);
      } else {
        Sentry.captureException(new Error("Unknown error occurred"));
        setError("An unknown error occurred.");
      }

      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, authToken, refreshAccessToken]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  return { data, loading, error, refetch: fetchUser };
};
